import { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface ProgressPieChartProps {
  progress: number; // 0-100 사이의 값
  completedColor?: string; // 완료된 부분 색상
  remainingColor?: string; // 남은 부분 색상
  width?: number; // 차트 너비
  height?: number; // 차트 높이
  showText?: boolean; // 중앙 텍스트 표시 여부
  fontSize?: number; // 폰트 크기
  fontColor?: string; // 폰트 색상
  decimalPlaces?: number; // 소수점 자리수
}

const ProgressPieChart = ({
  progress,
  completedColor = '#A5B3CE',
  remainingColor = '#E0E0E0',
  width = 300,
  height = 300,
  showText = true,
  fontSize = 60,
  fontColor = '#333',
  decimalPlaces = 1,
}: ProgressPieChartProps) => {
  const validProgress = useMemo(() => {
    // progress 값을 0-100 사이로 제한
    const validNum = Math.min(Math.max(progress, 0), 100);

    return parseFloat(validNum.toFixed(decimalPlaces));
  }, [progress]);

  const remaining = useMemo(() => 100 - validProgress, [validProgress]);

  const data = useMemo(
    () => ({
      labels: ['완료', '남은 부분'],
      datasets: [
        {
          data: [remaining, validProgress],
          backgroundColor: [remainingColor, completedColor],
          borderWidth: 0,
        },
      ],
    }),
    [remaining, validProgress, remainingColor, completedColor]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      circumference: 360,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          display: false,
        },
      },
    }),
    []
  );

  const containerStyle = useMemo(
    () => ({
      width: `${width}px`,
      height: `${height}px`,
      position: 'relative' as const,
    }),
    [width, height]
  );

  const overlayStyle = useMemo(
    () => ({
      display: showText ? ('block' as const) : ('none' as const),
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center' as const,
      pointerEvents: 'none' as const,
    }),
    [showText]
  );

  const percentageStyle = useMemo(
    () => ({
      fontSize: `${fontSize}px`,
      fontWeight: 'bold' as const,
      color: fontColor,
    }),
    [fontSize, fontColor]
  );

  return (
    <div style={containerStyle}>
      <Pie data={data} options={options} />
      <div style={overlayStyle}>
        <div style={percentageStyle}>{validProgress}%</div>
      </div>
    </div>
  );
};

export default ProgressPieChart;
